import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { DocumentTimelineStatus } from '@features/invoices/elements/DocumentTimelineStatus'
import { InvoiceEvent } from '@features/invoices/types/invoices/invoiceEvent'

import { useTrackInvoiceView } from '../../../../hooks/useTrackInvoiceView'
import { useSupportedInvoiceStatusEvents } from '../../hooks/useSupportedInvoiceStatusEvents'
import { invoiceStatusClarificationToTranslationKey } from './constants/invoiceStatusToTranslationKey'
import { getTimelineStatusByInvoiceEventType } from './utils/getTimelineStatusByInvoiceEvent'
import { getTimelineStatusTranslationKey } from './utils/getTimelineStatusTranslationKey'
import { hasInvoiceStatusEventClarification } from './utils/hasInvoiceSendingEventClarification'

export const InvoiceTimelineStatus = () => {
  const { t } = useTranslation()
  const { trackInvoiceView } = useTrackInvoiceView()

  const { documentType, invoiceBillingStage, isLoading, supportedStatusEvents } = useSupportedInvoiceStatusEvents()

  const getTimelineStatusItemTitle = useCallback(
    (event: InvoiceEvent): string =>
      t(getTimelineStatusTranslationKey(event.eventType, documentType, invoiceBillingStage)),
    [t, documentType, invoiceBillingStage],
  )

  const getTimelineItemDescription = useCallback(
    (event: InvoiceEvent): string | undefined => {
      if (hasInvoiceStatusEventClarification(event.eventType)) {
        return t(invoiceStatusClarificationToTranslationKey[event.eventType])
      }

      if (event.eventData?.recipient) {
        return event.eventData.recipient
      }

      return undefined
    },
    [t],
  )

  const getTimelineItemStatus = useCallback(
    (event: InvoiceEvent) => getTimelineStatusByInvoiceEventType(event.eventType),
    [],
  )

  return (
    <DocumentTimelineStatus
      events={supportedStatusEvents}
      getItemTitle={getTimelineStatusItemTitle}
      getItemDescription={getTimelineItemDescription}
      getItemStatus={getTimelineItemStatus}
      isLoading={isLoading}
      onCollapseChange={() => trackInvoiceView('Invoice timeline and history toggled')}
      title={t('external_invoices.view.sidebar.timeline.title')}
    />
  )
}
